import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useImperativeHandle, useMemo, useRef, } from 'react';
import { styled } from 'styled-components';
import { grid, space } from 'styled-system';
import { useSize } from '@hivebrite/react-hooks';
import { mobileDeviceFromUserAgent } from '@hivebrite/utils';
import { Image } from '@design-system/image';
import { useBreakpoint, useMatchBreakpoint, useTheme, } from '@design-system/theme';
import { Content } from './Content';
import { Footer } from './Footer';
import { Header } from './Header';
import { Dialog } from '../Dialog';
import { useIsScrollDown } from '../hooks/useIsScrollDown';
import { getBpNum } from '../utils/getBpNum';
import { getProperty } from '../utils/getProperty';
import { getWidth } from '../utils/getWidth';
export const Modal = forwardRef((_a, inputRef) => {
    var _b;
    var { bodyProps = {}, center = false, children, dataTestId = 'ds-modal', footProps = {}, footer, headProps = {}, header, isFixed, isLoading = false, isSticky, pb = 6, pl = { _: 4, m: 6 }, pr = { _: 4, m: 6 }, pt = 0, width = 'medium' } = _a, props = __rest(_a, ["bodyProps", "center", "children", "dataTestId", "footProps", "footer", "headProps", "header", "isFixed", "isLoading", "isSticky", "pb", "pl", "pr", "pt", "width"]);
    const breakpoint = useBreakpoint();
    const contentRef = useRef(null);
    const headerRef = useRef(null);
    const isSmall = useMatchBreakpoint('s');
    const labelledbyId = useId().replace(/[^a-zA-Z]/gu, '');
    const ref = useRef(null);
    const theme = useTheme();
    const viewportHeight = getProperty(document.body, '--viewport-height');
    const { getHeaderElement, getHeaderHeight } = (_b = headerRef.current) !== null && _b !== void 0 ? _b : {};
    const getMaxModalHeight = useCallback(() => {
        var _a, _b;
        const paddingBottom = (_a = theme.space[getBpNum(pb, breakpoint)]) !== null && _a !== void 0 ? _a : theme.space[6];
        if (getHeaderElement === null || getHeaderElement === void 0 ? void 0 : getHeaderElement()) {
            const headerHeight = (_b = getHeaderHeight === null || getHeaderHeight === void 0 ? void 0 : getHeaderHeight()) !== null && _b !== void 0 ? _b : 0;
            return Math.floor(mobileDeviceFromUserAgent.any()
                ? viewportHeight - headerHeight - paddingBottom
                : (viewportHeight / 100) * 90 - headerHeight - paddingBottom);
        }
        return Math.floor(mobileDeviceFromUserAgent.any()
            ? viewportHeight - paddingBottom
            : (viewportHeight / 100) * 90 - paddingBottom);
    }, [
        breakpoint,
        getHeaderElement,
        getHeaderHeight,
        pb,
        theme.space,
        viewportHeight,
    ]);
    const getModalElement = useCallback(() => { var _a, _b; return (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) !== null && _b !== void 0 ? _b : ref.current; }, [ref]);
    const getModalHeight = useCallback(() => { var _a, _b; return (_b = (_a = getModalElement()) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : 0; }, [getModalElement]);
    useImperativeHandle(inputRef, () => ({
        getHeaderElement,
        getHeaderHeight,
        getMaxModalHeight,
        getModalElement,
        getModalHeight,
    }), [
        getHeaderElement,
        getHeaderHeight,
        getMaxModalHeight,
        getModalElement,
        getModalHeight,
    ]);
    const { isScrollDown, viewportRef } = useIsScrollDown();
    const viewportSize = useSize(viewportRef);
    const withShadow = useMemo(() => getModalHeight() > (viewportSize === null || viewportSize === void 0 ? void 0 : viewportSize.height), [getModalHeight, viewportSize]);
    return (_jsxs(ModalStyled, Object.assign({ "$isFixed": isFixed, "$isSticky": isSticky, ariaLabelledby: labelledbyId, center: center, dataTestId: dataTestId, isModal: true, ref: ref, width: isSmall ? '100%' : getWidth(width) }, props, { children: [_jsx(Header, Object.assign({ id: labelledbyId, isLoading: isLoading, parentId: labelledbyId }, headProps, { "$withShadow": isScrollDown && withShadow, ref: headerRef, children: header })), _jsxs(Content, Object.assign({ isLoading: isLoading, isSmall: isSmall, parentId: labelledbyId, pb: pb, pl: pl, pr: pr, pt: pt }, bodyProps, { ref: contentRef, viewportRef: viewportRef, children: [header ? null : _jsx(FloatImage, {}), children] })), _jsx(Footer, Object.assign({ isLoading: isLoading, parentId: labelledbyId }, footProps, { children: footer }))] })));
});
const FloatImage = styled(Image).attrs({
    height: 60,
    mr: { _: -4, m: -6 },
    src: '',
    width: 60,
}) `
  ${space}
  float: right;
`;
const ModalStyled = styled(Dialog).attrs(({ $isFixed, $isSticky, ariaLabelledby }) => ({
    display: 'grid',
    gridTemplateAreas: "'modal-head' 'modal-body' 'modal-foot'",
    gridTemplateRows: {
        _: `auto var(--modal-calc-${ariaLabelledby}, 1fr) auto`,
        m: $isFixed
            ? `auto var(--modal-calc-${ariaLabelledby}, 1fr) auto`
            : $isSticky
                ? `auto min(var(--modal-body-${ariaLabelledby}, 0), var(--modal-calc-${ariaLabelledby}, 0)) auto`
                : `auto var(--modal-body-${ariaLabelledby}, 0) auto`,
    },
    minHeight: { _: 'var(--viewport-height, 100vh)', m: 'auto' },
}))(grid);
//# sourceMappingURL=Modal.js.map